/**
 * Call Analyzer Page (Admin Portal)
 * ==================================
 * Two-panel master-detail layout for analyzing Virtual Host calls.
 * Left panel: restaurant picker + phone filter + scrollable call list
 * Right panel: full analysis display matching analyze_call.py terminal output
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Phone,
  Clock,
  ShoppingCart,
  Tag,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Save,
  PhoneCall,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
} from 'lucide-react';
import { salesApi } from '@/services/api/salesApi';

// =========================================
// TYPES
// =========================================

interface Restaurant {
  rest_account_id: number;
  rest_id: number | null;
  restaurant_name: string;
  phone_number: string | null;
  city: string | null;
  state: string | null;
}

interface CallRecord {
  call_id: number;
  call_sid: string | null;
  customer_phone: string | null;
  customer_name: string | null;
  call_started_at: string | null;
  call_ended_at: string | null;
  call_duration_seconds: number | null;
  call_status: string | null;
  order_placed: boolean;
  has_analysis: boolean;
  order_id: number | null;
}

interface ConversationEvent {
  ts: string;
  delta_s?: number;
  speaker: 'customer' | 'ai' | 'tool' | 'speech';
  text?: string;
  name?: string;
  type?: string;
  params?: Record<string, any>;
  result?: Record<string, any>;
  from?: string;
  to?: string;
}

interface CallAnalysis {
  call_id: number;
  call_sid: string | null;
  customer_phone: string | null;
  customer_name: string | null;
  call_started_at: string | null;
  call_ended_at: string | null;
  call_duration_seconds: number | null;
  call_status: string | null;
  order_placed: boolean;
  hume_chat_id: string | null;
  analysis: {
    version: number;
    setup: Record<string, any>;
    conversation: ConversationEvent[];
    speech_stats: Record<string, number>;
    tools_summary: Record<string, number>;
    order: Record<string, any>;
    issues: Array<{ type: string; input: string; detail: string }>;
  } | null;
  analysis_source: string;
  order?: Record<string, any>;
  annotations: Array<{
    annotation_id: number;
    comments: string;
    tags: string[];
    annotated_by_name: string;
    created_at: string;
  }>;
}

// =========================================
// HELPERS
// =========================================

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const truncateSid = (sid: string | null): string => {
  if (!sid) return '--';
  return sid.length > 12 ? sid.slice(0, 12) + '...' : sid;
};

// =========================================
// MAIN COMPONENT
// =========================================

const CallAnalyzer: React.FC = () => {
  // State: restaurant search
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [showRestaurantDropdown, setShowRestaurantDropdown] = useState(false);
  const restaurantRef = useRef<HTMLDivElement>(null);

  // State: call search
  const [phoneSearch, setPhoneSearch] = useState('');
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [callsLoading, setCallsLoading] = useState(false);

  // State: analysis
  const [selectedCall, setSelectedCall] = useState<CallAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // State: annotation
  const [annotationText, setAnnotationText] = useState('');
  const [annotationTags, setAnnotationTags] = useState('');
  const [savingAnnotation, setSavingAnnotation] = useState(false);

  // Search restaurants with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (restaurantSearch.length >= 2) {
        try {
          const results = await salesApi.getAnalyzerRestaurants(restaurantSearch);
          setRestaurants(results);
          setShowRestaurantDropdown(true);
        } catch (err) {
          console.error('Restaurant search failed:', err);
        }
      } else {
        setRestaurants([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [restaurantSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (restaurantRef.current && !restaurantRef.current.contains(e.target as Node)) {
        setShowRestaurantDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectRestaurant = (r: Restaurant) => {
    setSelectedRestaurant(r);
    setRestaurantSearch(r.restaurant_name);
    setShowRestaurantDropdown(false);
    setCalls([]);
    setSelectedCall(null);
  };

  const searchCalls = useCallback(async () => {
    if (!selectedRestaurant) return;
    setCallsLoading(true);
    try {
      const result = await salesApi.getAnalyzerCalls({
        rest_account_id: selectedRestaurant.rest_account_id,
        phone: phoneSearch || undefined,
        limit: 50,
      });
      setCalls(result.calls);
      setCallsTotal(result.total);
    } catch (err) {
      console.error('Call search failed:', err);
    } finally {
      setCallsLoading(false);
    }
  }, [selectedRestaurant, phoneSearch]);

  // Auto-search when restaurant selected
  useEffect(() => {
    if (selectedRestaurant) {
      searchCalls();
    }
  }, [selectedRestaurant]);

  const loadAnalysis = async (callId: number) => {
    setAnalysisLoading(true);
    try {
      const data = await salesApi.getCallAnalysis(callId);
      setSelectedCall(data);
      setAnnotationText('');
      setAnnotationTags('');
    } catch (err) {
      console.error('Analysis load failed:', err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const saveAnnotation = async () => {
    if (!selectedCall || !annotationText.trim()) return;
    setSavingAnnotation(true);
    try {
      const tags = annotationTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      await salesApi.saveAnnotation(selectedCall.call_id, {
        comments: annotationText.trim(),
        tags,
      });
      await loadAnalysis(selectedCall.call_id);
      setAnnotationText('');
      setAnnotationTags('');
    } catch (err) {
      console.error('Save annotation failed:', err);
    } finally {
      setSavingAnnotation(false);
    }
  };

  // =========================================
  // RENDER
  // =========================================

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-130px)]">
      {/* ── LEFT PANEL ── */}
      <div className="w-full lg:w-80 shrink-0 flex flex-col gap-3">
        {/* Restaurant Search */}
        <div ref={restaurantRef} className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={restaurantSearch}
              onChange={(e) => {
                setRestaurantSearch(e.target.value);
                if (selectedRestaurant && e.target.value !== selectedRestaurant.restaurant_name) {
                  setSelectedRestaurant(null);
                  setCalls([]);
                  setSelectedCall(null);
                }
              }}
              placeholder="Search restaurant..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 outline-none"
            />
          </div>
          {showRestaurantDropdown && restaurants.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white rounded-lg shadow-xl max-h-60 overflow-auto">
              {restaurants.map((r) => (
                <button
                  key={r.rest_account_id}
                  onClick={() => selectRestaurant(r)}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                >
                  <div className="font-medium text-gray-900 text-sm">{r.restaurant_name}</div>
                  <div className="text-xs text-gray-500">
                    {r.phone_number || 'No phone'} {r.city && `· ${r.city}, ${r.state}`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Filter */}
        {selectedRestaurant && (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
              <input
                type="text"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchCalls()}
                placeholder="Filter by phone..."
                className="w-full pl-9 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 outline-none"
              />
            </div>
            <button
              onClick={searchCalls}
              disabled={callsLoading}
              className="px-3 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 disabled:opacity-50 text-sm font-medium shrink-0"
            >
              {callsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>
        )}

        {/* Call List */}
        {selectedRestaurant && (
          <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
            {callsLoading && (
              <div className="flex items-center justify-center py-8 text-white/60">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...
              </div>
            )}
            {!callsLoading && calls.length === 0 && (
              <div className="text-center text-white/40 text-sm py-8">No calls found</div>
            )}
            {calls.map((call) => {
              const isActive = selectedCall?.call_id === call.call_id;
              return (
                <button
                  key={call.call_id}
                  onClick={() => loadAnalysis(call.call_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all text-sm ${
                    isActive
                      ? 'border-[#FDB913] bg-[#FDB913]/15 text-white'
                      : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {call.customer_phone || 'Unknown'}
                        {call.customer_name && (
                          <span className="text-white/50 ml-1">({call.customer_name})</span>
                        )}
                      </div>
                      <div className="text-xs text-white/40 flex items-center gap-2 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatDate(call.call_started_at)}
                        <span className="text-white/20">|</span>
                        {formatDuration(call.call_duration_seconds)}
                      </div>
                    </div>
                    {call.order_placed && (
                      <ShoppingCart className="w-3.5 h-3.5 text-green-400 shrink-0 ml-2" />
                    )}
                  </div>
                </button>
              );
            })}
            {!callsLoading && calls.length > 0 && (
              <div className="text-xs text-white/30 text-center py-2">
                {callsTotal} calls · showing {calls.length}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 overflow-y-auto min-h-0 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
        {/* Loading */}
        {analysisLoading && (
          <div className="flex items-center justify-center h-full text-white/60">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading analysis...
          </div>
        )}

        {/* Empty State */}
        {!analysisLoading && !selectedCall && (
          <div className="flex flex-col items-center justify-center h-full text-white/30">
            <PhoneCall className="w-12 h-12 mb-3" />
            <div className="text-lg font-medium">Select a call to view analysis</div>
            <div className="text-sm mt-1">
              {selectedRestaurant
                ? 'Click any call from the list on the left'
                : 'Start by searching for a restaurant'}
            </div>
          </div>
        )}

        {/* Analysis Display */}
        {!analysisLoading && selectedCall && (
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCall.call_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="p-5 space-y-5"
            >
              {/* ── HEADER ── */}
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-bold text-base">
                      VH CALL ANALYZER — {formatDate(selectedCall.call_started_at)}
                    </div>
                    <div className="text-slate-300 text-sm mt-1">
                      SID: {truncateSid(selectedCall.call_sid)}
                      <span className="mx-2 text-slate-500">|</span>
                      {formatDuration(selectedCall.call_duration_seconds)}
                      <span className="mx-2 text-slate-500">|</span>
                      {selectedCall.customer_phone || '--'}
                      {selectedCall.customer_name && (
                        <span className="text-slate-400 ml-2">({selectedCall.customer_name})</span>
                      )}
                    </div>
                    {selectedRestaurant && (
                      <div className="text-slate-400 text-sm mt-0.5">
                        Restaurant: {selectedRestaurant.restaurant_name}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <CopyAllButton call={selectedCall} restaurantName={selectedRestaurant?.restaurant_name} />
                    {selectedCall.analysis_source === 'buffer' && (
                      <span className="px-2.5 py-1 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                        Full Analysis
                      </span>
                    )}
                    {selectedCall.analysis_source === 'hume_available' && (
                      <span className="px-2.5 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full font-medium">
                        Hume Only
                      </span>
                    )}
                    {selectedCall.analysis_source === 'none' && (
                      <span className="px-2.5 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full font-medium">
                        No Analysis
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* ── ANALYSIS CONTENT ── */}
              {selectedCall.analysis ? (
                <>
                  {/* CALL SETUP — compact, always open */}
                  <AccordionSection title="CALL SETUP" emoji="📞">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-white/70">
                      <div>Voice: <span className="text-white/90">{selectedCall.analysis.setup?.voice || '--'}</span></div>
                      <div>Menu items: <span className="text-white/90">{selectedCall.analysis.setup?.menu_item_count || '--'}</span></div>
                      <div>Popular: <span className="text-white/90">{selectedCall.analysis.setup?.popular_item_count || '--'} ⭐</span></div>
                      <div>Prompt: <span className="text-white/90">{selectedCall.analysis.setup?.prompt_version || '--'}</span></div>
                      <div>Corrections: <span className="text-white/90">{selectedCall.analysis.setup?.speech_corrections_count || 0}</span></div>
                      <div>Aliases: <span className="text-white/90">{selectedCall.analysis.setup?.aliases_count || 0}</span></div>
                    </div>
                  </AccordionSection>

                  {/* ISSUES — top of dashboard for quick scanning */}
                  <AccordionIssuesSection issues={selectedCall.analysis.issues || []} />

                  {/* SPEECH INTELLIGENCE */}
                  <AccordionSection title="SPEECH INTELLIGENCE" emoji="🔤">
                    <SpeechStats stats={selectedCall.analysis.speech_stats || {}} />
                  </AccordionSection>

                  {/* TOOLS CALLED */}
                  <AccordionSection title={`TOOLS CALLED (${Object.values(selectedCall.analysis.tools_summary || {}).reduce((a, b) => Number(a) + Number(b), 0)} total)`} emoji="🛠️">
                    <ToolsSummary tools={selectedCall.analysis.tools_summary || {}} />
                  </AccordionSection>

                  {/* ORDER SUMMARY */}
                  <AccordionSection title="ORDER SUMMARY" emoji="📊">
                    <OrderSummary order={selectedCall.analysis.order} dbOrder={selectedCall.order} />
                  </AccordionSection>

                  {/* CONVERSATION — collapsed by default, with Copy button */}
                  <AccordionSection
                    title="CONVERSATION"
                    emoji="💬"
                    defaultOpen={false}
                    headerRight={
                      <CopyConversationButton events={selectedCall.analysis.conversation || []} />
                    }
                  >
                    <ConversationTimeline events={selectedCall.analysis.conversation || []} />
                  </AccordionSection>
                </>
              ) : (
                /* Pre-buffer call — no analysis */
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <div className="text-yellow-300 text-sm font-medium">
                    {selectedCall.analysis_source === 'hume_available'
                      ? 'Pre-buffer call — Hume transcript available but no structured analysis.'
                      : 'No analysis data available for this call.'}
                  </div>
                  <div className="text-yellow-300/60 text-xs mt-1">
                    Calls made after the buffer deployment will have full analysis.
                  </div>
                </div>
              )}

              {/* ── ANNOTATIONS ── */}
              <Section title="ANNOTATIONS" emoji="💬">
                {/* Existing annotations */}
                {selectedCall.annotations && selectedCall.annotations.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {selectedCall.annotations.map((a) => (
                      <div key={a.annotation_id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white/80 text-sm">{a.annotated_by_name}</span>
                          <span className="text-xs text-white/30">{a.created_at ? formatDate(a.created_at) : ''}</span>
                        </div>
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{a.comments}</p>
                        {a.tags && a.tags.length > 0 && (
                          <div className="flex gap-1.5 mt-2">
                            {a.tags.map((tag, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                                <Tag className="w-3 h-3" /> {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* New annotation form */}
                <div className="space-y-2">
                  <textarea
                    value={annotationText}
                    onChange={(e) => setAnnotationText(e.target.value)}
                    placeholder="Add analysis notes, findings, or action items..."
                    rows={3}
                    className="w-full px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/30 text-sm focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 outline-none resize-none"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={annotationTags}
                      onChange={(e) => setAnnotationTags(e.target.value)}
                      placeholder="Tags (comma separated): bug, speech-fix, prompt-issue..."
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/30 text-sm focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 outline-none"
                    />
                    <button
                      onClick={saveAnnotation}
                      disabled={!annotationText.trim() || savingAnnotation}
                      className="px-4 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 disabled:opacity-50 flex items-center gap-2 text-sm font-medium shrink-0"
                    >
                      {savingAnnotation ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save
                    </button>
                  </div>
                </div>
              </Section>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

// =========================================
// SUB-COMPONENTS
// =========================================

/** Collapsible accordion section */
const AccordionSection: React.FC<{
  title: string;
  emoji: string;
  defaultOpen?: boolean;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, emoji, defaultOpen = true, headerRight, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/5 transition-colors"
      >
        <h3 className="text-sm font-bold text-white/90 flex items-center gap-1.5">
          {emoji} {title}
        </h3>
        <div className="flex items-center gap-2">
          {headerRight && <span onClick={(e) => e.stopPropagation()}>{headerRight}</span>}
          {isOpen ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/** Build conversation as plain text */
const buildConversationText = (events: ConversationEvent[]): string => {
  const lines: string[] = [];
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    if (e.speaker === 'customer' || e.speaker === 'ai') {
      const delta = e.delta_s && e.delta_s > 0 ? ` (+${e.delta_s.toFixed(1)}s)` : '';
      const label = e.speaker === 'customer' ? 'Customer' : 'AI';
      lines.push(`[${e.ts}]${delta} ${label}: ${e.text || ''}`);
    } else if (e.speaker === 'tool') {
      const params = e.params ? JSON.stringify(e.params) : '';
      lines.push(`  🔧 ${e.name}(${params})`);
      if (e.result) {
        const r = e.result;
        if (r.success === true) {
          const info = r.matched_item
            ? `${r.matched_item}${r.section ? ` (${r.section})` : ''} @ $${Number(r.price || 0).toFixed(2)}`
            : r.status || 'success';
          lines.push(`    └─ ✅ Matched → ${info}`);
        } else if (r.success === false) {
          if (r.error === 'not_found') {
            lines.push(`    └─ ❌ REJECTED: "${r.input || e.params?.item_name || ''}" not in menu`);
          } else {
            lines.push(`    └─ ❌ ${r.error || 'failed'}`);
          }
        }
      }
    } else if (e.speaker === 'speech') {
      if (i > 0 && events[i - 1].speaker === 'tool') continue;
      lines.push(`  🔤 ${e.type}: "${e.from}" → "${e.to}"`);
    }
  }
  return lines.join('\n');
};

/** Build full analysis as plain text for clipboard */
const buildFullAnalysisText = (
  call: CallAnalysis,
  restaurantName: string | undefined,
): string => {
  const lines: string[] = [];
  const sep = '═'.repeat(60);

  // Header
  lines.push(sep);
  lines.push(`VH CALL ANALYZER — ${formatDate(call.call_started_at)}`);
  lines.push(`SID: ${call.call_sid || '--'} | ${formatDuration(call.call_duration_seconds)} | ${call.customer_phone || '--'}${call.customer_name ? ` (${call.customer_name})` : ''}`);
  if (restaurantName) lines.push(`Restaurant: ${restaurantName}`);
  lines.push(sep);

  if (!call.analysis) {
    lines.push('\n(No analysis data available)');
    return lines.join('\n');
  }

  const a = call.analysis;

  // Call Setup
  lines.push('\n📞 CALL SETUP');
  const s = a.setup || {};
  lines.push(`  Voice: ${s.voice || '--'} | Menu items: ${s.menu_item_count || '--'} | Popular: ${s.popular_item_count || '--'}`);
  lines.push(`  Prompt: ${s.prompt_version || '--'} | Corrections: ${s.speech_corrections_count || 0} | Aliases: ${s.aliases_count || 0}`);

  // Issues
  const issues = a.issues || [];
  lines.push(`\n${issues.length > 0 ? '⚠️' : '✅'} ISSUES (${issues.length})`);
  if (issues.length > 0) {
    issues.forEach((iss) => lines.push(`  • ${iss.type.toUpperCase()}: '${iss.input}' ${iss.detail}`));
  } else {
    lines.push('  None detected');
  }

  // Speech Intelligence
  const stats = a.speech_stats || {};
  const statEntries = Object.entries(stats);
  if (statEntries.length > 0) {
    lines.push('\n🔤 SPEECH INTELLIGENCE');
    statEntries.forEach(([k, v]) => lines.push(`  ${k.replace(/_/g, ' ')}: ${v}`));
  }

  // Tools Called
  const tools = a.tools_summary || {};
  const toolEntries = Object.entries(tools).sort((x, y) => Number(y[1]) - Number(x[1]));
  if (toolEntries.length > 0) {
    const total = toolEntries.reduce((sum, [, c]) => sum + Number(c), 0);
    lines.push(`\n🛠️ TOOLS CALLED (${total} total)`);
    toolEntries.forEach(([name, count]) => lines.push(`  ${name.padEnd(30)} × ${count}`));
  }

  // Order Summary
  const order = a.order;
  if (order && order.items && order.items.length > 0) {
    lines.push('\n📊 ORDER SUMMARY');
    order.items.forEach((item: any) => {
      lines.push(`  ${item.name} × ${item.qty} @ $${Number(item.price).toFixed(2)} = $${(Number(item.price) * Number(item.qty)).toFixed(2)}`);
    });
    lines.push(`  Total: ${order.items.length} items | $${Number(order.subtotal || 0).toFixed(2)}`);
    if (order.customer_name) lines.push(`  Customer: ${order.customer_name}${order.pickup_time ? ` · Pickup: ${order.pickup_time}` : ''}`);
  } else if (call.order) {
    lines.push('\n📊 ORDER SUMMARY');
    lines.push(`  Order #${call.order.order_id} · Status: ${call.order.order_status || '--'} · Total: $${Number(call.order.order_total || 0).toFixed(2)}`);
  }

  // Conversation
  const events = a.conversation || [];
  if (events.length > 0) {
    lines.push('\n💬 CONVERSATION');
    lines.push(buildConversationText(events));
  }

  // Annotations
  if (call.annotations && call.annotations.length > 0) {
    lines.push('\n💬 ANNOTATIONS');
    call.annotations.forEach((ann) => {
      lines.push(`  [${formatDate(ann.created_at)}] ${ann.annotated_by_name}: ${ann.comments}`);
      if (ann.tags.length > 0) lines.push(`    Tags: ${ann.tags.join(', ')}`);
    });
  }

  return lines.join('\n');
};

/** Copy conversation to clipboard */
const CopyConversationButton: React.FC<{ events: ConversationEvent[] }> = ({ events }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildConversationText(events));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-0.5 text-xs text-white/50 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
      title="Copy conversation to clipboard"
    >
      {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/** Copy full analysis to clipboard */
const CopyAllButton: React.FC<{ call: CallAnalysis; restaurantName: string | undefined }> = ({ call, restaurantName }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildFullAnalysisText(call, restaurantName));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 text-white/70 hover:text-white rounded-lg border border-white/10 transition-colors"
      title="Copy full analysis to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy All'}
    </button>
  );
};

/** Issues section with accordion — always open, styled by severity */
const AccordionIssuesSection: React.FC<{ issues: Array<{ type: string; input: string; detail: string }> }> = ({ issues }) => {
  const hasIssues = issues && issues.length > 0;
  return (
    <AccordionSection
      title={`ISSUES (${issues.length})`}
      emoji={hasIssues ? '⚠️' : '✅'}
      defaultOpen={true}
    >
      {hasIssues ? (
        <div className="space-y-1">
          {issues.map((issue, i) => (
            <div key={i} className="text-sm text-red-400 font-mono">
              • {issue.type.toUpperCase()}: '{issue.input}' {issue.detail}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-green-400 text-sm font-mono">None detected</div>
      )}
    </AccordionSection>
  );
};

/** Non-collapsible section (for annotations form) */
const Section: React.FC<{ title: string; emoji: string; children: React.ReactNode }> = ({ title, emoji, children }) => (
  <div>
    <h3 className="text-sm font-bold text-white/90 mb-2.5">
      {emoji} {title}
    </h3>
    {children}
  </div>
);

/** Conversation timeline — matches analyze_call.py output */
const ConversationTimeline: React.FC<{ events: ConversationEvent[] }> = ({ events }) => {
  if (!events || events.length === 0) {
    return <div className="text-white/30 text-sm text-center py-4">(no conversation data)</div>;
  }

  // Group: tool/speech events that follow a speaker line are "children" of that speaker
  // But we render them inline, indented, just like the terminal output
  return (
    <div className="space-y-0 font-mono text-[13px] leading-relaxed max-h-[600px] overflow-y-auto pr-2">
      {events.map((event, i) => {
        if (event.speaker === 'customer' || event.speaker === 'ai') {
          const isCustomer = event.speaker === 'customer';
          const deltaStr = event.delta_s && event.delta_s > 0
            ? `+${event.delta_s.toFixed(1)}s`
            : '';
          return (
            <div key={i} className="flex items-start gap-0 py-0.5">
              <span className="text-white/30 w-[52px] shrink-0 text-right mr-2">[{event.ts}]</span>
              <span className="text-white/25 w-[52px] shrink-0 text-right mr-2 tabular-nums">
                {deltaStr}
              </span>
              <span className="mr-1.5">{isCustomer ? '👤' : '🤖'}</span>
              <span className={isCustomer ? 'text-blue-300' : 'text-purple-300'}>
                <span className="font-semibold">{isCustomer ? 'Customer' : 'AI'}:</span>{' '}
                {event.text && event.text.length > 140 ? event.text.slice(0, 137) + '...' : event.text}
              </span>
            </div>
          );
        }

        if (event.speaker === 'tool') {
          const paramsStr = event.params
            ? JSON.stringify(event.params).slice(0, 80) + (JSON.stringify(event.params).length > 80 ? '...' : '')
            : '';

          // Collect details: result + any speech events immediately after
          const details: Array<{ type: string; content: React.ReactNode }> = [];

          // Tool result
          if (event.result) {
            const r = event.result;
            if (r.success === true) {
              const matchInfo = r.matched_item
                ? `${r.matched_item}${r.section ? ` (${r.section})` : ''} @ $${Number(r.price || 0).toFixed(2)}`
                : r.status || 'success';
              details.push({
                type: 'matched',
                content: <span className="text-green-400">✅ Matched → {matchInfo}</span>,
              });
              if (r.match_type === 'fuzzy') {
                details.push({
                  type: 'fuzzy',
                  content: <span className="text-white/30">📝 (fuzzy match — names differed)</span>,
                });
              }
            } else if (r.success === false) {
              if (r.error === 'not_found') {
                details.push({
                  type: 'rejected',
                  content: <span className="text-red-400">❌ REJECTED: "{r.input || event.params?.item_name || ''}" not in menu</span>,
                });
              } else if (r.error === 'multiple_matches' || r.error === 'ambiguous') {
                details.push({
                  type: 'ambiguous',
                  content: <span className="text-yellow-400">🔀 AMBIGUOUS: multiple matches found</span>,
                });
              } else {
                details.push({
                  type: 'error',
                  content: <span className="text-red-400">❌ {r.error || 'failed'}</span>,
                });
              }
            }
          }

          // Look ahead for speech events that belong to this tool call
          let j = i + 1;
          while (j < events.length && events[j].speaker === 'speech') {
            const sp = events[j];
            if (sp.type === 'alias') {
              details.push({
                type: 'alias',
                content: <span className="text-green-400">🔤 Alias: "{sp.from}" → "{sp.to}"</span>,
              });
            } else if (sp.type === 'hearing') {
              details.push({
                type: 'hearing',
                content: <span className="text-yellow-400">🔤 Hearing: "{sp.from}" → "{sp.to}"</span>,
              });
            } else if (sp.type === 'post_correction') {
              details.push({
                type: 'post_correction',
                content: <span className="text-green-400">🔤 Post-correction: "{sp.from}" → "{sp.to}"</span>,
              });
            } else {
              details.push({
                type: 'speech',
                content: <span className="text-green-400">🔤 {sp.type}: "{sp.from}" → "{sp.to}"</span>,
              });
            }
            j++;
          }

          return (
            <div key={i} className="py-0.5">
              {/* Tool call line — indented to align under conversation */}
              <div className="flex items-start">
                <span className="w-[112px] shrink-0" />
                <span className="text-amber-400">
                  🔧 {event.name}({paramsStr && `{${paramsStr.slice(1, -1)}}`})
                </span>
              </div>
              {/* Details with tree connectors */}
              {details.map((d, di) => {
                const isLast = di === details.length - 1;
                return (
                  <div key={`${i}-d${di}`} className="flex items-start">
                    <span className="w-[112px] shrink-0" />
                    <span className="text-white/20 mr-1.5 ml-2">
                      {isLast ? '└─' : '├─'}
                    </span>
                    {d.content}
                  </div>
                );
              })}
            </div>
          );
        }

        // Standalone speech event (not attached to tool)
        if (event.speaker === 'speech') {
          // Check if this was already rendered as a child of the previous tool
          // by seeing if the previous event was a tool
          if (i > 0 && events[i - 1].speaker === 'tool') {
            return null; // Already rendered as part of the tool above
          }
          return (
            <div key={i} className="flex items-start py-0.5">
              <span className="w-[112px] shrink-0" />
              <span className="text-green-400">
                🔤 {event.type}: "{event.from}" → "{event.to}"
              </span>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

/** Speech intelligence summary */
const SpeechStats: React.FC<{ stats: Record<string, number> }> = ({ stats }) => {
  const entries = Object.entries(stats);
  if (entries.length === 0) {
    return <div className="text-white/30 text-sm">(no speech data)</div>;
  }
  return (
    <div className="font-mono text-sm space-y-0.5">
      {entries.map(([key, val]) => {
        const label = key.replace(/_/g, ' ');
        const isAlert = (key.includes('rejected') || key.includes('ambiguous') || key.includes('failed')) && Number(val) > 0;
        return (
          <div key={key} className="flex">
            <span className="text-white/50 w-[200px]">{label}:</span>
            <span className={`font-medium ${isAlert ? 'text-red-400' : Number(val) > 0 ? 'text-green-400' : 'text-white/25'}`}>
              {String(val)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/** Tools summary */
const ToolsSummary: React.FC<{ tools: Record<string, number> }> = ({ tools }) => {
  const sorted = Object.entries(tools).sort((a, b) => Number(b[1]) - Number(a[1]));
  if (sorted.length === 0) {
    return <div className="text-white/30 text-sm">(no tool calls)</div>;
  }
  return (
    <div className="font-mono text-sm space-y-0.5">
      {sorted.map(([name, count]) => (
        <div key={name} className="flex">
          <span className="text-white/70 w-[220px]">{name}</span>
          <span className="text-amber-400">× {String(count)}</span>
        </div>
      ))}
    </div>
  );
};

/** Order summary */
const OrderSummary: React.FC<{ order: Record<string, any> | undefined; dbOrder: Record<string, any> | undefined }> = ({ order, dbOrder }) => {
  if (!order || !order.items || order.items.length === 0) {
    if (dbOrder) {
      return (
        <div className="text-sm text-white/70">
          <div>Order #{dbOrder.order_id} · Status: {dbOrder.order_status || '--'}</div>
          <div>Total: ${Number(dbOrder.order_total || 0).toFixed(2)}</div>
          {dbOrder.pickup_time && <div>Pickup: {dbOrder.pickup_time}</div>}
        </div>
      );
    }
    return <div className="text-white/30 text-sm">(no order placed)</div>;
  }

  return (
    <div className="font-mono text-sm">
      {order.items.map((item: any, i: number) => (
        <div key={i} className="text-white/70">
          {item.name} × {item.qty} @ ${Number(item.price).toFixed(2)} = ${(Number(item.price) * Number(item.qty)).toFixed(2)}
        </div>
      ))}
      <div className="text-white font-bold mt-1.5 pt-1.5 border-t border-white/10">
        Total: {order.items.length} items | ${Number(order.subtotal || 0).toFixed(2)}
      </div>
      {order.customer_name && (
        <div className="text-white/50 mt-1">
          Customer: {order.customer_name}
          {order.pickup_time && ` · Pickup: ${order.pickup_time}`}
        </div>
      )}
    </div>
  );
};


export default CallAnalyzer;
